<template>
  <div class="view-header">
    <div class="view-header-right">
      <span
        id="year-label"
        class="sr-only"
        >{{ $t('events.selectYear') }}</span
      >
      <div id="year-select">
        <button
          id="year-select-button"
          ref="year-select-button"
          type="button"
          aria-labelledby="year-label year-select-button"
          :aria-haspopup="showDropdownButton ? 'listbox' : undefined"
          :aria-expanded="showDropdownButton ? showDropdown.toString() : undefined"
          :aria-controls="showDropdownButton ? 'year-listbox' : undefined"
          class="right-dropdown"
          @click.exact.stop.prevent="toggleDropdown()"
          @keydown.down.exact.stop.prevent="openDropdown()"
          @keydown.up.exact.stop.prevent="openDropdown()"
          @keydown.tab="handleBlur()"
        >
          <CalendarIcon
            class="right-dropdown-icon"
            aria-hidden="true"
            focusable="false"
          />
          <div class="right-dropdown-text">
            {{ selectedYear }}
            <DropdownArrowIcon v-if="showDropdownButton" />
          </div>
        </button>
        <div
          v-show="showDropdown"
          id="year-listbox"
          v-click-outside="handleBlur"
          role="listbox"
          aria-labelledby="year-label"
          class="right-dropdown-content"
        >
          <p
            v-for="year in YEARS"
            :ref="(el) => setYearOptionEl(year, el)"
            :key="year"
            :aria-selected="`${year === selectedYear}`"
            role="option"
            tabindex="-1"
            class="right-dropdown-content-item"
            @click.exact.stop.prevent="changeYear(year)"
            @keydown.enter.exact.prevent.stop="changeYear(year)"
            @keydown.space.exact.prevent.stop="changeYear(year)"
            @keydown.down.exact.prevent.stop="arrowNavigation(year + 1)"
            @keydown.up.exact.prevent.stop="arrowNavigation(year - 1)"
            @keydown.home.exact.prevent.stop="arrowNavigation(YEARS[0])"
            @keydown.end.exact.prevent.stop="arrowNavigation(YEARS[YEARS.length - 1])"
            @keydown.tab="handleBlur()"
            @keydown.esc.exact.prevent.stop="closeAndReturnFocus()"
          >
            {{ year }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, useTemplateRef } from 'vue';
// Components
import CalendarIcon from '../../icons/CalendarIcon.vue';
import DropdownArrowIcon from '../../icons/DropdownArrowIcon.vue';
import { useEventsStore } from '../../../../../stores/eventsStore.js';

defineOptions({
  name: 'YearDropdown',
});

// TODO: How many years should we handle? - where should this be stored?
const YEARS = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];

const eventsStore = useEventsStore();
const route = useRoute();
const yearSelectButtonRef = useTemplateRef('year-select-button');

const showDropdown = ref(false);

const queryParams = computed(() => eventsStore.queryParams);
const currentView = computed(() => eventsStore.currentView);
const listViewType = computed(() => eventsStore.listViewType);
const getCurrentYear = computed(() => eventsStore.getCurrentYear);

const showDropdownButton = computed(() => !(listViewType.value === 'all' && currentView.value === 'EventsListView'));
const selectedYear = computed(() => (showDropdownButton.value ? getCurrentYear.value : new Date().getFullYear()));

// Option elements keyed by year. getCurrentInstance() is null outside setup, so refs are collected here instead.
const yearOptionEls = new Map();

function setYearOptionEl(year, el) {
  if (el) {
    yearOptionEls.set(year, el);
  } else {
    yearOptionEls.delete(year);
  }
}

function resolveYearOptionEl(year) {
  return yearOptionEls.get(year);
}

function toggleDropdown(forceClose = false) {
  if (forceClose || !showDropdownButton.value) {
    showDropdown.value = false;
    return;
  }
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) focusSelectedYear();
}

function openDropdown() {
  if (!showDropdownButton.value || showDropdown.value) return;
  toggleDropdown();
}

function focusSelectedYear() {
  nextTick(() => {
    resolveYearOptionEl(getCurrentYear.value)?.focus?.();
  });
}

function closeAndReturnFocus() {
  toggleDropdown(true);
  yearSelectButtonRef.value?.focus?.();
}

function changeYear(year) {
  eventsStore.updateDateFilters({
    year,
    target: yearSelectButtonRef.value?.id,
  });
  eventsStore.setSelectedYearUpdated(true);
  closeAndReturnFocus();
  return navigateTo({
    query: {
      ...route.query,
      ...queryParams.value,
    },
  });
}

function handleBlur() {
  toggleDropdown(true);
}

function arrowNavigation(year) {
  if (YEARS.indexOf(year) === -1) return;
  const el = resolveYearOptionEl(year);
  el?.focus?.();
}
</script>

<style lang="scss" scoped>
.view-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-right: 24px;
  .view-header-left,
  .view-header-right {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
  .view-header-right {
    position: relative;
    cursor: pointer;
    .right-dropdown {
      display: flex;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      align-items: center;
      .right-dropdown-text {
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 16px;
        letter-spacing: 0.4px;
        margin-left: 8px;
        display: flex;
        align-items: center;
      }
    }
    .right-dropdown-content {
      position: absolute;
      display: flex;
      flex-direction: column;
      min-width: 70px;
      top: 40px;
      right: 0;
      cursor: pointer;
      background-color: #f9f9f9;
      box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
      padding: 12px 16px;
      z-index: 1;
      .right-dropdown-content-item {
        min-height: 30px;
      }
    }
  }
}
</style>
